<script setup lang="ts">
import { useField } from 'vee-validate'
import { computed, ref, watch } from 'vue'

const emit = defineEmits<(e: 'update:modelValue', value: string | number) => void>()

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'text',
    validator(value: string) {
      return ['text', 'password', 'email', 'number'].includes(value)
    },
  },
  error: {
    type: String,
    default: '',
  },
  isSheba: {
    type: Boolean,
  },
  isTag: {
    type: Boolean,
  },
})

const isFocused = ref(false)
const showPassword = ref(false)

const { value, errorMessage, handleBlur } = useField<string | number>(props.name, undefined, {
  initialValue: props.modelValue,
})

const hasError = computed(() => !!errorMessage.value)

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const getInputType = () => {
  if (props.type === 'password') {
    return showPassword.value ? 'text' : 'password'
  }
  return props.type
}

watch(value, (v) => {
  emit('update:modelValue', v as string | number)
})
</script>

<template>
      <div class="relative flex flex-col gap-1 w-full h-[71.47px]">
            <!-- label -->
            <label v-if="label"
                  class="w-fit bg-white px-1 transition-all duration-400 z-50 text-[#1b325a] pointer-events-none"
                  :class="(isFocused || value) ? 'absolute ms-4 -mt-2 text-[10px] font-bold' : 'absolute mt-[13px] ms-5 text-[13px] font-medium'">
                  {{ label }}
            </label>

            <!-- input container -->
            <div class="relative">
                  <!-- input -->
                  <textarea :name="name" :type="getInputType()" :disabled="disabled" v-model="value"
                        @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
                        @focus="isFocused = true" @blur="() => { isFocused = false; handleBlur(); }" class="rounded-xl bg-white border px-[18px] min-h-[122px] text-[13px] font-medium outline-none transition
                    border-[#d5ddea] focus:outline-none focus:ring-0 focus:border-black
                    disabled:bg-gray-100 disabled:cursor-not-allowed w-full pt-4!" />

                  <!-- eye icon for password -->
                  <button v-if="type === 'password'" type="button" @click="togglePasswordVisibility"
                        class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none">
                        <svg v-if="showPassword" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                        <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                  </button>
                  <span v-if="isSheba" class="absolute left-3 top-1/2 transform -translate-y-1/2"
                        :class="hasError ? 'text-red-500' : (isFocused ? 'text-blue-600' : '')">
                        IR
                  </span>
                  <span v-if="isTag" class="absolute left-3 top-1/2 transform -translate-y-1/2"
                        :class="hasError ? 'text-red-500' : (isFocused ? 'text-blue-600' : '')">
                        @
                  </span>
            </div>

            <!-- error -->
            <ErrorMessage :name="name" />
      </div>
</template>
